import React from "react";
import { useNavigate } from "react-router-dom";

import {
  countWords,
  formatDate,
  getExcerptByChars,
  stripHtml,
} from "@Utils/functions";
import useGetBlogPosts from "@Hooks/useLenderBlogPosts";
import { LenderBlogPost } from "@Types/lender_blogposts";

import styles from "./BlogList.module.scss";

const BlogPost: React.FC<{ post: LenderBlogPost }> = ({ post }) => {
  const navigate = useNavigate();
  const plainTextBody = stripHtml(post.data.info.body ?? "");

  const handleOpenPost = () =>
    navigate(`/release-notes/${post._id}`, { replace: true });

  return (
    <article className={styles.post}>
      <h3 className={styles.postTitle}>{post.data.info.title}</h3>
      <div className={styles.meta}>
        <span className={styles.date}>
          {formatDate(post.createdAt, "medium")}
        </span>
        <span className={styles.bullet}>•</span>
        <span className={styles.wordCount}>
          {countWords(plainTextBody)} words
        </span>
        <span className={styles.bullet}>•</span>
        <span className={styles.readTime}>3 min read</span>
      </div>
      <p className={styles.excerpt}>{getExcerptByChars(plainTextBody, 100)}</p>
      <a onClick={handleOpenPost} className={styles.readMore}>
        Read more
        <svg
          className={styles.arrow}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </a>
    </article>
  );
};

const BlogList: React.FC = () => {
  const {
    data: posts,
    isLoading,
    error,
  } = useGetBlogPosts({ query: { "data.info.published": true } });

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading blog posts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>Failed to load blog posts. Please try again later.</p>
      </div>
    );
  }

  return (
    <section className={styles.blogList}>
      <h2 className={styles.title}>Release notes</h2>
      <p>
        Stay updated with the latest features, improvements, and bug fixes in
        our app.
      </p>
      <div className={styles.posts}>
        {posts?.map((post) => (
          <BlogPost key={post._id} post={post} />
        ))}
      </div>
    </section>
  );
};

export default BlogList;
